// AI-CONTEXT: See docs/features/update-aanvragen.md
// De statuswaarden van een updateverzoek, hun Nederlandse labels en de vragen
// die de rest van de feature erover stelt.
//
// Dit bestand is de BRON. `functions/updateRequests/` spiegelt deze waarden —
// src/ en functions/ zijn losse npm-pakketten zonder gedeelde modulegrens, dus
// niets verbindt de twee bij de build en een afwijking is stil. De spiegel
// wordt vastgepind door een pariteitstest aan de functions-kant; die faalt als
// deze lijst verandert. Repareer dan de spiegel, nooit dit bestand.
// AI-CONTEXT: See docs/patterns.md#data-mirrored-constants

/**
 * @typedef {'nieuw' | 'in behandeling' | 'meer uitleg nodig' | 'gebouwd' | 'afgerond' | 'afgewezen'} UpdateRequestStatus
 */

/**
 * In de volgorde van de doorloop, niet alfabetisch — de UI toont ze zo.
 *
 * 'meer uitleg nodig' = de bal ligt bij de organisatie: Deccos heeft een vraag
 * gesteld en kan pas verder met een antwoord. De toelichting IS die vraag, en
 * staat daarom in het verzoekenoverzicht van de organisatie zelf.
 * 'gebouwd' = klaar, maar de klant ziet het pas na de volgende release.
 * 'afgerond' betekent sindsdien: live. Alleen op die overgang gaan melding en
 * mail naar de aanvrager.
 * AI-CONTEXT: See docs/features/update-aanvragen-afgerond-mail.md
 */
const UPDATE_REQUEST_STATUSES = /** @type {Array<UpdateRequestStatus>} */ ([
    'nieuw',
    'in behandeling',
    'meer uitleg nodig',
    'gebouwd',
    'afgerond',
    'afgewezen',
])

/** Waarmee een net ingediend verzoek begint. */
const DEFAULT_UPDATE_REQUEST_STATUS = 'nieuw'

/**
 * Statussen die nog werk betekenen — 'gebouwd' moet nog live.
 *
 * 'meer uitleg nodig' telt mee, ook al ligt de bal bij de organisatie: valt het
 * uit de telling op de Overzicht-kaart, dan is het verzoek voor Deccos
 * onzichtbaar en blijft het liggen zodra de klant niet antwoordt.
 */
const OPEN_UPDATE_REQUEST_STATUSES = /** @type {Array<UpdateRequestStatus>} */ ([
    'nieuw',
    'in behandeling',
    'meer uitleg nodig',
    'gebouwd',
])

/** @type {Record<UpdateRequestStatus, string>} */
const UPDATE_REQUEST_STATUS_LABELS = {
    'nieuw': 'Nieuw',
    'in behandeling': 'In behandeling',
    'meer uitleg nodig': 'Meer uitleg nodig',
    'gebouwd': 'Gebouwd',
    'afgerond': 'Afgerond',
    'afgewezen': 'Afgewezen',
}

/** Wat er in beeld komt als de opgeslagen status niet klopt. */
const UNKNOWN_STATUS_LABEL = 'Onbekend'

/**
 * @param {*} status - Waarde uit een UpdateRequests-document
 * @returns {boolean} True als het een van de vijf afgesproken waarden is
 */
const isUpdateRequestStatus = (status) => UPDATE_REQUEST_STATUSES.includes(status)

/**
 * Of dit verzoek nog openstaat.
 *
 * Een onbekende waarde telt NIET mee. Dat is dataschade, en meetellen zou het
 * getal op de Overzicht-kaart opblazen zonder dat iemand ziet waardoor. Het
 * verzoek zelf blijft gewoon in de lijsten staan, dus het raakt niet zoek.
 *
 * @param {*} status - Waarde uit een UpdateRequests-document
 * @returns {boolean}
 */
const isOpenUpdateRequestStatus = (status) => OPEN_UPDATE_REQUEST_STATUSES.includes(status)

/** Statussen waarbij de toelichting niet mag ontbreken. */
const STATUSES_REQUIRING_NOTE = /** @type {Array<UpdateRequestStatus>} */ ([
    // Zonder uitleg is een afwijzing voor de indiener een dood spoor.
    'afgewezen',
    // Hier IS de toelichting de vraag. Zonder tekst leest de organisatie een
    // status die zegt dat er iets van haar verwacht wordt, zonder dat er iets
    // gevraagd is.
    'meer uitleg nodig',
])

/**
 * Of deze status een toelichting verplicht maakt.
 *
 * De harde weigering staat in de Cloud Function — de UI mag hem niet als enige
 * bewaken.
 *
 * @param {*} status - Nieuwe status
 * @returns {boolean}
 */
const requiresStatusNote = (status) => STATUSES_REQUIRING_NOTE.includes(status)

/**
 * Of de toelichting bij deze status een vraag aan de organisatie is in plaats
 * van een mededeling. Bepaalt hoe het verzoekenoverzicht hem toont.
 *
 * @param {*} status - Waarde uit een UpdateRequests-document
 * @returns {boolean}
 */
const statusNoteIsQuestion = (status) => status === 'meer uitleg nodig'

/**
 * Waarom deze status niet zonder toelichting kan.
 *
 * Per status anders: 'geef een toelichting' zegt bij een vraag niet wat er
 * verwacht wordt. De Cloud Function spiegelt deze teksten en gebruikt ze als
 * foutmelding, zodat het scherm en de poort hetzelfde zeggen; de pariteitstest
 * aan de functions-kant pint dat vast.
 *
 * @param {*} status - Nieuwe status
 * @returns {string} Lege string als deze status geen toelichting eist
 */
const missingStatusNoteReason = (status) => {
    if (status === 'meer uitleg nodig') {
        return 'Zet de vraag aan de organisatie in de toelichting, anders weet de aanvrager niet wat hij moet aanvullen.'
    }
    if (status === 'afgewezen') {
        return 'Een afwijzing heeft een toelichting nodig, anders weet de aanvrager niet waarom.'
    }
    return ''
}

/**
 * Het label dat de gebruiker leest.
 *
 * LET OP: dit is een weergavewaarde, geen sorteersleutel. 'Onbekend' is gewone
 * tekst en sorteert vóór de echte labels, dus sorteer op de status zelf.
 * AI-CONTEXT: See docs/pitfalls.md#ui-display-fallback-used-as-sort-key
 *
 * @param {*} status - Waarde uit een UpdateRequests-document
 * @returns {string} Nederlands label; nooit leeg
 */
const updateRequestStatusLabel = (status) => (
    isUpdateRequestStatus(status) ? UPDATE_REQUEST_STATUS_LABELS[status] : UNKNOWN_STATUS_LABEL
)

export {
    UPDATE_REQUEST_STATUSES,
    DEFAULT_UPDATE_REQUEST_STATUS,
    OPEN_UPDATE_REQUEST_STATUSES,
    UPDATE_REQUEST_STATUS_LABELS,
    UNKNOWN_STATUS_LABEL,
    STATUSES_REQUIRING_NOTE,
    isUpdateRequestStatus,
    isOpenUpdateRequestStatus,
    requiresStatusNote,
    statusNoteIsQuestion,
    missingStatusNoteReason,
}

export default updateRequestStatusLabel
